import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import useReps from '../../../hooks/useReps';

export default function LeadsFormModal({ isOpen, lead, onClose, onSubmit }) {
  const reps = useReps();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    profession: '',
    experience: '',
    source: 'Website',
    rep: '',
    value: '',
    status: 'New',
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lead) {
      setFormData(lead);
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        profession: '',
        experience: '',
        source: 'Website',
        rep: '',
        value: '',
        status: 'New',
      });
    }
    setErrors({});
  }, [lead, isOpen]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    // Sprint 15 QA: phone-only leads (e.g. from CSV imports) must be editable.
    const hasEmail = formData.email.trim();
    const hasPhone = formData.phone.trim();
    if (!hasEmail && !hasPhone) newErrors.email = 'Email or phone is required';
    if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Valid email required';
    if (hasPhone && !/^[+\d][\d\s-]{6,}$/.test(formData.phone)) newErrors.phone = 'Valid phone required';
    if (!formData.company.trim()) newErrors.company = 'Company is required';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSaving(true);
    try {
      await onSubmit(formData);
    } catch {
      // Parent surfaces the error; the modal stays open for retry.
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">
          {lead ? 'Edit Lead' : 'Add New Lead'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Name *</label>
            <Input
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Lead name"
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Email *</label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="email@example.com"
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Phone *</label>
            <Input
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(555) 123-4567"
              className={errors.phone ? 'border-red-500' : ''}
            />
            {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Company *</label>
            <Input
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Company name"
              className={errors.company ? 'border-red-500' : ''}
            />
            {errors.company && <p className="text-red-500 text-sm mt-1">{errors.company}</p>}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Profession</label>
            <Input
              name="profession"
              value={formData.profession}
              onChange={handleChange}
              placeholder="Job title"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Experience</label>
            <Input
              name="experience"
              value={formData.experience}
              onChange={handleChange}
              placeholder="e.g., 5 years"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Source</label>
            <Select
              name="source"
              value={formData.source}
              onChange={(value) => handleSelectChange("source", value)}
              options={[
                { value: 'Website', label: 'Website' },
                { value: 'Referral', label: 'Referral' },
                { value: 'Event', label: 'Event' },
                { value: 'Social Media', label: 'Social Media' },
                { value: 'Cold Call', label: 'Cold Call' },
              ]}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Assigned Rep</label>
            <Select
              name="rep"
              value={formData.rep}
              onChange={(value) => handleSelectChange("rep", value)}
              options={[
                { value: '', label: 'Unassigned' },
                ...(reps || []).map((r) => ({ value: r, label: r })),
              ]}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Estimated Value</label>
            <Input
              name="value"
              type="number"
              value={formData.value}
              onChange={handleChange}
              placeholder="$"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Status</label>
            <Select
              name="status"
              value={formData.status}
              onChange={(value) => handleSelectChange("status", value)}
              options={[
                { value: 'New', label: 'New' },
                { value: 'Contacted', label: 'Contacted' },
                { value: 'Qualified', label: 'Qualified' },
                { value: 'Unqualified', label: 'Unqualified' },
              ]}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              onClick={onClose}
              disabled={saving}
              className="flex-1 bg-gray-200 text-gray-800 hover:bg-gray-300"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={saving}
              className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
            >
              {saving ? 'Saving…' : lead ? 'Update Lead' : 'Add Lead'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
